import React, { useEffect } from "react";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { Spacer } from "../../offering/spacer";
import styles from "./empty_meeting_rooms.module.css";
import { MeetingRoomsFilter } from "../../offering/template_offering_listing_meeting_room";
import { useAnalytics } from "../../lib/analytics/analytics";
import { useMrGalleryImprovementsV2FeatureFlag } from "@/core/splitio";
import { Button } from "react-bootstrap";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { useMediaQuery } from "@/hooks";
import classNames from "classnames";

interface EmptyMeetingRoomsProps {
  filter: MeetingRoomsFilter;
  hasFilter: boolean;
  onClearFilters: () => void;
  onClearOneFilter: (filter: MeetingRoomsFilter) => void;
}

export function EmptyMeetingRooms({
  filter,
  hasFilter,
  onClearFilters,
  onClearOneFilter,
}: EmptyMeetingRoomsProps) {
  const { fbt } = useI18n();
  const { device } = useMediaQuery();
  const analytics = useAnalytics();

  const meetingRoomGalleryImprovementsV2FeatureFlag =
    useMrGalleryImprovementsV2FeatureFlag();

  useEffect(() => {
    analytics.event("Meeting Rooms Empty State", {
      City: filter.city,
      "Min Capacity": filter.minCapacity,
      "Max Capacity": filter.maxCapacity,
      Date: filter.date,
      "Start Time": filter.startTime,
      "End Time": filter.endTime,
      "Location UUID": filter.locationID,
      Source: "WL",
    });
  }, [
    analytics,
    filter.city,
    filter.minCapacity,
    filter.maxCapacity,
    filter.date,
    filter.startTime,
    filter.endTime,
    filter.locationID,
  ]);

  if (meetingRoomGalleryImprovementsV2FeatureFlag) {
    return (
      <div
        className={classNames("container", device !== "mobile" ? "p-4" : "p-3")}
        style={{ backgroundColor: "rgba(var(--bs-primary-rgb), 0.15)" }}
      >
        <div className="fs-2 fw-bold">
          {fbt("Looks like there aren’t any exact matches.")}
        </div>
        <div className="mt-4">
          {fbt(
            "Consider adjusting your filters or browsing similar spaces below."
          )}
        </div>
        <div className="mt-4 d-flex flex-wrap gap-3">
          {filter.startTime && (
            <Button
              variant="outline bg-white"
              style={{
                borderColor: "var(--bs-secondary)",
                color: "var(--bs-secondary)",
              }}
              onClick={() =>
                onClearOneFilter({
                  startTime: undefined,
                  endTime: undefined,
                })
              }
            >
              {fbt("Clear start time")} <IoIosCloseCircleOutline />
            </Button>
          )}
          {filter.endTime && (
            <Button
              variant="outline bg-white"
              style={{
                borderColor: "var(--bs-secondary)",
                color: "var(--bs-secondary)",
              }}
              onClick={() =>
                onClearOneFilter({
                  endTime: undefined,
                })
              }
            >
              {fbt("Clear duration")} <IoIosCloseCircleOutline />
            </Button>
          )}
          {(filter.date || (filter.startDate && filter.endDate)) && (
            <Button
              variant="outline bg-white"
              style={{
                borderColor: "var(--bs-secondary)",
                color: "var(--bs-secondary)",
              }}
              onClick={() =>
                onClearOneFilter({
                  date: undefined,
                  startDate: undefined,
                  endDate: undefined,
                })
              }
            >
              {fbt("Clear date")} <IoIosCloseCircleOutline />
            </Button>
          )}
          {(filter.cities || filter.locationIDs) && (
            <Button
              variant="outline bg-white"
              style={{
                borderColor: "var(--bs-secondary)",
                color: "var(--bs-secondary)",
              }}
              onClick={() =>
                onClearOneFilter({ locationIDs: undefined, cities: undefined })
              }
            >
              {fbt("Clear locations")} <IoIosCloseCircleOutline />
            </Button>
          )}
          {(filter.maxCapacity || filter.minCapacity) && (
            <Button
              variant="outline bg-white"
              style={{
                borderColor: "var(--bs-secondary)",
                color: "var(--bs-secondary)",
              }}
              onClick={() =>
                onClearOneFilter({
                  maxCapacity: undefined,
                  minCapacity: undefined,
                })
              }
            >
              {fbt("Clear number of guests")} <IoIosCloseCircleOutline />
            </Button>
          )}

          <Button
            variant="outline bg-white"
            style={{
              borderColor: "var(--bs-secondary)",
              color: "var(--bs-secondary)",
            }}
            onClick={onClearFilters}
          >
            {fbt("Clear all filters")} <IoIosCloseCircleOutline />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className={styles.title}>{fbt("No meeting rooms found")}</div>
      <Spacer size={16} />
      <div>{fbt("Try changing or clearing some of your filters")}</div>
      {hasFilter && (
        <>
          <Spacer size={16} />
          <button className={styles.clearAllBtn} onClick={onClearFilters}>
            {fbt("Clear all filters")}
          </button>
        </>
      )}
    </div>
  );
}
